import {captureException} from '@sentry/browser';
import {cva} from 'platform/shared';

import {HTMLAttributeAnchorTarget, MouseEventHandler} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {isURLDangerous, Nullish, suffixTestId, TestIdProps, useOnMount} from 'shared/web';

import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {ThemeIconKey} from '../../utils/foundationTheme';
import {HStack} from '../HStack/HStack';
import {Icon} from '../Icon/Icon';
import {Show} from '../Show/Show';
import {Text} from '../Text/Text';

const linkVariants = cva(
  'inline-block text-[var(--color-general-accent)] focus:[box-shadow:none]',
  {
    variants: {
      isDisabled: {
        true: 'cursor-not-allowed opacity-70 hover:no-underline',
        false: 'cursor-pointer hover:underline',
      },
      overflowWrap: {
        anywhere: '[overflow-wrap:anywhere] [word-break:break-word]',
        initial: '[overflow-wrap:initial] [word-break:initial]',
      },
      hasConstrainedWidth: {
        true: 'max-w-full',
        false: '[max-width:initial]',
      },
    },
    defaultVariants: {
      isDisabled: false,
      overflowWrap: 'initial',
      hasConstrainedWidth: false,
    },
  }
);

export interface LinkProps extends TestIdProps {
  href?: string | null;
  title?: string | Nullish;
  children?: string;
  size?: 'base' | 'small' | 'xSmall';
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  leftIcon?: ThemeIconKey;
  rightIcon?: ThemeIconKey;
  iconSize?: Integer | ValueByDevice<Integer>;
  /** Use when the link has no visible text (icon-only); sets `aria-label` on the anchor. */
  ariaLabel?: string;
  overflowWrap?: 'anywhere';
  /** Truncates the label to a single line with an ellipsis (requires a width-constrained parent). */
  noWrap?: boolean;
  target?: HTMLAttributeAnchorTarget;
  download?: string | boolean;
  isDisabled?: boolean;
  rel?: string;
}

function LinkHrefValidator(props: Pick<LinkProps, 'href'>) {
  useOnMount(function rejectDangerousHref() {
    if (isURLDangerous(props.href)) {
      captureException(new Error(`Link component got potentially dangerous URL: ${props.href}`));
      throw new Error('Invalid URL');
    }
  });

  return null;
}

export function Link(props: LinkProps) {
  const hasVisibleLabel = Boolean(props.title || props.children);
  const isIconOnly = !hasVisibleLabel && (isNotNil(props.leftIcon) || isNotNil(props.rightIcon));

  return (
    <>
      <LinkHrefValidator key={props.href} href={props.href} />
      <a
        role="link" // eslint-disable-line jsx-a11y/no-redundant-roles -- Preserve the legacy Link DOM contract during migration
        rel={props.rel}
        target={props.target}
        download={props.download}
        href={props.isDisabled ? undefined : (props.href ?? undefined)}
        onClick={props.isDisabled ? undefined : props.onClick}
        aria-disabled={props.isDisabled}
        aria-label={isIconOnly ? props.ariaLabel : undefined}
        className={linkVariants({
          isDisabled: Boolean(props.isDisabled),
          overflowWrap: props.overflowWrap ?? 'initial',
          hasConstrainedWidth: Boolean(props.noWrap || props.overflowWrap === 'anywhere'),
        })}
        data-testid={suffixTestId('link', props)}
      >
        <HStack spacing={1} align="center">
          <Show when={isNotNil(props.leftIcon)}>
            <Icon size={props.iconSize ?? 4} color="general.accent" value={props.leftIcon} />
          </Show>
          <Show when={hasVisibleLabel}>
            <Text
              color="link"
              size={props.size}
              noWrap={props.noWrap}
              overflowWrap={props.overflowWrap}
              title={props.noWrap ? (props.title ?? props.children ?? undefined) : undefined}
              data-testid={suffixTestId('linkContent', props)}
            >
              {props.title || props.children}
            </Text>
          </Show>
          <Show when={isNotNil(props.rightIcon)}>
            <Icon size={props.iconSize ?? 4} color="general.accent" value={props.rightIcon} />
          </Show>
        </HStack>
      </a>
    </>
  );
}
